import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Instagram, Loader2, MessageCircle, Send, Users } from 'lucide-react';
import { cambiarVisibilidad, leerComunidad, pedirContacto } from './comunidad.api';

/* Marketing › Comunidad: los logos (y el nombre abajo) de las empresas que trabajan con Lisual, por país y las más nuevas
   primero. Invita a sumarse al grupo de WhatsApp y al canal de Instagram, y "¿Querés comunicarte con alguna de estas
   empresas?" le avisa a Lisual por mail para conectar clientes con proveedores, desarrolladoras y constructoras.
   Cada empresa puede salir del directorio (abajo de todo). */

export const LINK_WHATSAPP = 'https://chat.whatsapp.com/IFmNqZ5dVsP5pOW2FULxeA';
export const LINK_INSTAGRAM = 'https://www.instagram.com/channel/AbaQknRItoalnThT/';

// Sin logo (o si no carga): iniciales del nombre hasta que la empresa suba el suyo
const iniciales = (n) => String(n || '').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '·';

const Tarjeta = ({ e, elegida, onElegir }) => {
  const [sinLogo, setSinLogo] = useState(false);
  return (
  <button
    type="button"
    onClick={() => onElegir(e)}
    className={`group relative flex flex-col items-center rounded-2xl border bg-white p-3 text-center transition-shadow hover:shadow-md ${elegida ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200'}`}
    data-comunidad-empresa={e.id}
  >
    {e.nueva && <span className="absolute left-2 top-2 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Nueva</span>}
    <div className={`flex h-20 w-full items-center justify-center rounded-xl ${e.oscuro && e.logo && !sinLogo ? 'bg-gray-900 px-2' : ''}`}>
      {e.logo && !sinLogo ? (
        <img src={e.logo} alt={e.nombre} loading="lazy" className="max-h-20 max-w-full object-contain" onError={() => setSinLogo(true)} />
      ) : (
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-slate-100 text-lg font-bold text-indigo-700" aria-hidden="true">{iniciales(e.nombre)}</span>
      )}
    </div>
    <span className="mt-2 line-clamp-2 text-xs font-medium text-gray-800">{e.nombre}</span>
    {e.link && (
      <a href={e.link} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()} className="absolute right-2 top-2 text-gray-400 hover:text-pink-600" aria-label={`Instagram de ${e.nombre}`}>
        <Instagram size={14} />
      </a>
    )}
  </button>
  );
};

const Comunidad = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(false);
  const [pais, setPais] = useState('Todos');
  const [elegida, setElegida] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [envio, setEnvio] = useState('listo'); // listo | enviando | enviado | error
  const [visible, setVisible] = useState(null);

  useEffect(() => {
    leerComunidad().then((d) => { setDatos(d); setVisible(d.miEmpresa ? d.miEmpresa.visible : null); }).catch(() => setError(true));
  }, []);

  const paises = useMemo(() => (datos?.paises || []).map((p) => p.pais), [datos]);
  const grupos = useMemo(() => (datos?.paises || []).filter((p) => pais === 'Todos' || p.pais === pais), [datos, pais]);

  const contactar = async () => {
    setEnvio('enviando');
    try {
      await pedirContacto(elegida?.id || null, mensaje);
      setEnvio('enviado');
    } catch {
      setEnvio('error');
    }
  };
  const alternarVisible = async () => {
    const nuevo = !visible;
    setVisible(nuevo);
    try { await cambiarVisibilidad(nuevo); } catch { setVisible(!nuevo); }
  };

  return (
    <div className="space-y-5" data-comunidad>
      {/* Invitación */}
      <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-indigo-900 p-5 text-white sm:p-7">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"><Users size={13} />Comunidad Lisual</span>
        <h2 className="mt-3 text-xl font-bold leading-tight sm:text-2xl">Sumate a nuestra comunidad</h2>
        <p className="mt-2 max-w-2xl text-sm text-gray-300">
          Constructoras, desarrolladoras, estudios y proveedores que muestran su obra con Lisual. Compartimos novedades, obras y oportunidades para trabajar juntos.
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <a href={LINK_WHATSAPP} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-400" data-comunidad-whatsapp>
            <MessageCircle size={16} />Unite al grupo de WhatsApp
          </a>
          <a href={LINK_INSTAGRAM} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20" data-comunidad-instagram>
            <Instagram size={16} />Seguí el canal de Instagram
          </a>
        </div>
      </div>

      {/* Contacto */}
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4 sm:p-5" data-comunidad-contacto>
        <h3 className="text-base font-semibold text-gray-900">¿Querés comunicarte con alguna de estas empresas?</h3>
        {envio === 'enviado' ? (
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-emerald-800"><CheckCircle2 size={16} />¡Listo! Te escribimos para ponerte en contacto{elegida ? ` con ${elegida.nombre}` : ''}.</p>
        ) : (
          <>
            <p className="mt-1 text-sm text-gray-600">
              {elegida ? <>Elegiste <b>{elegida.nombre}</b>. </> : 'Tocá el logo de la empresa (o dejalo así si buscás un rubro). '}
              Nosotros te conectamos.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input value={mensaje} onChange={(e) => setMensaje(e.target.value)} maxLength={500} placeholder="Contanos qué buscás (opcional)" className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm" />
              <button type="button" onClick={contactar} disabled={envio === 'enviando'} className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60" data-comunidad-contactar>
                {envio === 'enviando' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}Quiero que me contacten
              </button>
            </div>
            {envio === 'error' && <p className="mt-2 text-xs text-red-600">No se pudo enviar. Probá de nuevo.</p>}
          </>
        )}
      </div>

      {/* Directorio */}
      {error ? (
        <p className="text-sm text-gray-500">No pudimos cargar la comunidad. Probá de nuevo en un rato.</p>
      ) : !datos ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" />Cargando la comunidad…</div>
      ) : (
        <>
          <p className="px-1 text-sm text-gray-600" data-comunidad-aviso>Estas son algunas de las empresas que trabajan con Lisual.</p>
          <div className="-mx-0.5 flex gap-1.5 overflow-x-auto px-0.5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="País">
            {['Todos', ...paises].map((p) => (
              <button key={p} type="button" role="radio" aria-checked={pais === p} onClick={() => setPais(p)}
                className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${pais === p ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                {p}
              </button>
            ))}
          </div>
          {grupos.map((g) => (
            <section key={g.pais}>
              <h3 className="mb-2 px-1 text-sm font-semibold text-gray-700">{g.pais} <span className="font-normal text-gray-400">· {g.empresas.length}</span></h3>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-6">
                {g.empresas.map((e) => <Tarjeta key={e.id} e={e} elegida={elegida?.id === e.id} onElegir={(x) => setElegida(elegida?.id === x.id ? null : x)} />)}
              </div>
            </section>
          ))}
        </>
      )}

      {/* Mi empresa */}
      {visible !== null && (
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700" data-comunidad-visible>
          <span>Mostrar mi empresa en la Comunidad{datos?.miEmpresa && !datos.miEmpresa.tieneLogo ? ' (todavía sin logo: aparecés con tu nombre)' : ''}</span>
          <input type="checkbox" checked={visible} onChange={alternarVisible} className="h-5 w-5 accent-indigo-600" />
        </label>
      )}
    </div>
  );
};

export default Comunidad;
